import { useEffect, useRef, useState } from 'react';
import CustomScrollBar from '../component/CustomScrollBar';
import { IReportResourceItem } from '../type';
import Image from '../../../image';
import { ChevronLeftSIcon, ChevronRightSIcon } from 'tdesign-icons-react';

const ReportResourceItem = ({ item }: { item: IReportResourceItem }) => {
  const { cover } = item || {};
  const title = item.title.replace(/<br\s*\/?>/g, '\n');
  const handleClick = () => {
    window.open(item.url, '_blank');
  };
  return (
    <div
      className="flex flex-col flex-shrink-0 cursor-pointer select-none"
      onClick={handleClick}
    >
      <Image
        loading="lazy"
        src={cover}
        alt={title}
        width={278}
        height={393}
        className="w-[278px] h-[393px] object-cover mobile:w-[240px] mobile:h-[339px]"
      />
      <div className="w-[278px] mobile:w-[240px] text-center text-[#121C59] text-lg mt-[20px] mobile:text-sm mobile:mt-2 font-400 line-clamp-2 whitespace-pre-line">
        {title}
      </div>
    </div>
  );
};

const ReportResourceList = ({
  list = [],
  className,
}: {
  list: IReportResourceItem[];
  className?: string;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [prevDisabled, setPrevDisabled] = useState(false);
  const [nextDisabled, setNextDisabled] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const scrollElement = scrollRef.current;

    const handleScroll = () => {
      if (scrollElement) {
        const scrollLeft = scrollElement.scrollLeft;
        const offsetWidth = scrollElement.offsetWidth;
        const scrollWidth = scrollElement.scrollWidth;
        setPrevDisabled(scrollLeft <= 0);
        setNextDisabled(scrollLeft + offsetWidth >= scrollWidth - 1);
      }
    };

    // 防抖处理函数
    const debouncedHandleScroll = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        handleScroll();
      }, 100);
    };

    handleScroll();
    if (scrollElement) {
      scrollElement.addEventListener('scroll', debouncedHandleScroll);
      window.addEventListener('resize', debouncedHandleScroll);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', debouncedHandleScroll);
      }
      window.removeEventListener('resize', debouncedHandleScroll);
    };
  }, [scrollRef]);

  const MOBILE_WIDTH = 1272;

  const onNext = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: MOBILE_WIDTH,
        behavior: 'smooth',
      });
    }
  };
  const onPrev = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: -MOBILE_WIDTH,
        behavior: 'smooth',
      });
    }
  };
  return (
    <div className={`w-full relative ${className}`}>
      <div
        className="flex flex-nowrap gap-10 mobile:gap-3 overflow-x-auto mobile:pb-0 scrollbar-hidden mobile:px-5"
        ref={scrollRef}
      >
        {list.map((item) => (
          <ReportResourceItem item={item} key={item.id}></ReportResourceItem>
        ))}
      </div>
      {/* PC端 */}
      <div className="mobile:hidden">
        {/* 上一页 */}
        <div
          className={`${
            prevDisabled ? 'invisible opacity-0' : 'visible opacity-100'
          }  transition-all duration-300`}
        >
          <div
            className={`size-10 rounded-full border-[1.5px] border-solid flex items-center justify-center cursor-pointer border-[#121C59] text-[#121C59] absolute -left-20 top-1/2 -translate-y-1/2`}
            onClick={onPrev}
          >
            <ChevronLeftSIcon size={24}></ChevronLeftSIcon>
          </div>
          <div className="absolute w-20 h-full left-0 top-0 z-10 bg-gradient-to-l from-transparent to-[#EDF3FF]"></div>
        </div>
        {/* 下一页 */}
        <div
          className={`${
            nextDisabled ? 'invisible opacity-0' : 'visible opacity-100'
          }  transition-all duration-300`}
        >
          <div
            className={`size-10 rounded-full border-[1.5px] border-solid flex items-center justify-center cursor-pointer border-[#121C59] text-[#121C59] absolute -right-20 top-1/2 -translate-y-1/2`}
            onClick={onNext}
          >
            <ChevronRightSIcon size={24}></ChevronRightSIcon>
          </div>
          <div className="absolute w-20 h-full right-0 top-0 z-10 bg-gradient-to-r from-transparent to-[#EDF3FF]"></div>
        </div>
      </div>
      {/* 移动端滚动条 */}
      <CustomScrollBar scrollRef={scrollRef}></CustomScrollBar>
    </div>
  );
};

export default ReportResourceList;
